import * as React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Transaction } from '@/types/transaction';
import { BankAccount } from '@/types/plaid'; // Assuming type location
import { normalizeCategory } from "@/lib/utils";
// NOTE: Need to define or import categoryEmojis and accountTypeIcons if they are not globally available
// For now, defining simplified versions based on previous context
// import { categoryEmojis, accountTypeIcons } from '@/lib/constants';
import { Landmark, CreditCard, HandCoins, LineChart, HelpCircle } from 'lucide-react'; // Import icons directly

// Move interface definition here
interface UserCategorySetting {
  name: string; // Normalized name like "Food & Drink"
  emoji: string;
}

interface TransactionDetailDialogProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
  userCategorySettings: Map<string, UserCategorySetting>; // Use locally defined type
  bankAccounts: BankAccount[];
}

// Helper to format date (moved from TransactionsPage)
const formatDetailDate = (dateString: string | undefined): string => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString + 'T00:00:00Z');
        return date.toLocaleDateString("en-US", {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
        });
    } catch (e) {
        console.error("Error parsing date string for detail:", dateString, e);
        return dateString;
    }
};

// Simplified default emojis (replace with actual import if available)
const defaultCategoryEmojis: Record<string, string> = {
  FOOD_AND_DRINK: "🍔",
  TRAVEL: "✈️",
  SHOPPING: "🛒",
  SERVICES: "💼",
  TRANSFER_OUT: "💸",
  TRANSFER_IN: "💵",
  INCOME: "💰",
  RENT_AND_UTILITIES: "🏠",
  GENERAL_MERCHANDISE: "🎁",
  OTHER: "🤷",
  UNCATEGORIZED: "🤷",
};


// Internal Account Icon Mapping (subset needed)
const internalAccountTypeIcons: Record<string, React.ElementType> = {
  depository: Landmark,
  credit: CreditCard,
  loan: HandCoins,
  investment: LineChart,
  other: HelpCircle,
};

export function TransactionDetailDialog({
  transaction,
  isOpen,
  onClose,
  userCategorySettings,
  bankAccounts,
}: TransactionDetailDialogProps) {

  // Internal helper functions using props
  const getAccountInfo = React.useCallback((plaidAccountId: string): BankAccount | undefined => {
    return bankAccounts.find(acc => acc.plaidAccountId === plaidAccountId);
  }, [bankAccounts]);

  const getAccountIcon = React.useCallback((accountType: string | undefined): React.ElementType => {
    return accountType ? internalAccountTypeIcons[accountType] || HelpCircle : HelpCircle;
  }, []);

  const getEmojiForCategory = React.useCallback((rawCategoryKey: string): string => {
    const userSetting = userCategorySettings.get(rawCategoryKey);
    if (userSetting?.emoji) {
      return userSetting.emoji;
    }
    return defaultCategoryEmojis[rawCategoryKey] || defaultCategoryEmojis['UNCATEGORIZED'] || '🤷';
  }, [userCategorySettings]);


  if (!transaction) {
    return null; // Don't render anything if no transaction is selected
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Transaction Details</DialogTitle>
          <DialogDescription>
             {transaction.merchant_name || transaction.name || "Details"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4 text-sm">
          <div className="grid grid-cols-[100px_1fr] items-center gap-4">
             <span className="text-muted-foreground">Date</span>
             <span>{formatDetailDate(transaction.date)}</span>
          </div>
          <div className="grid grid-cols-[100px_1fr] items-center gap-4">
              <span className="text-muted-foreground">Amount</span>
              <span className={`font-semibold ${transaction.amount < 0 ? '' : 'text-green-600'}`}>
                  {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: transaction.iso_currency_code || 'USD',
                  }).format(transaction.amount)}
                  {transaction.pending && <Badge variant="secondary" className="ml-2">Pending</Badge>}
              </span>
          </div>
          <div className="grid grid-cols-[100px_1fr] items-center gap-4">
             <span className="text-muted-foreground">Merchant</span>
             <div className="flex items-center gap-2">
                {transaction.logo_url && (
                    <img
                        src={transaction.logo_url}
                        alt={`${transaction.merchant_name || transaction.name || 'Transaction'} logo`}
                        className="h-6 w-6 rounded-sm object-contain border flex-shrink-0"
                        onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                )}
                <span>{transaction.merchant_name || transaction.name || <span className="text-muted-foreground italic">N/A</span>}</span>
             </div>
          </div>
           <Separator />
           <div className="grid grid-cols-[100px_1fr] items-center gap-4">
               <span className="text-muted-foreground">Category</span>
               {(() => {
                  const rawCat = transaction.personal_finance_category?.primary || transaction.category?.[0] || 'UNCATEGORIZED';
                  return (
                     <div className="flex items-center">
                        <span className="mr-2 text-base" role="img" aria-label={rawCat}>
                           {getEmojiForCategory(rawCat)}
                        </span>
                        <Badge variant="outline">
                           {normalizeCategory(rawCat)}
                        </Badge>
                     </div>
                  );
               })()}
           </div>
           <Separator />
           <div className="grid grid-cols-[100px_1fr] items-center gap-4">
              <span className="text-muted-foreground">Account</span>
              {(() => {
                  const account = getAccountInfo(transaction.account_id);
                  if (!account) {
                      return <span className="text-muted-foreground italic">Unknown Account</span>;
                  }
                  const AccountIcon = getAccountIcon(account.type);
                  return (
                      <div className="flex items-center gap-2">
                          <AccountIcon className="h-4 w-4 text-muted-foreground" />
                          <span>{account.name} ({account.mask ? `**** ${account.mask}` : 'N/A'})</span>
                      </div>
                  );
              })()}
           </div>
           <div className="grid grid-cols-[100px_1fr] items-center gap-4">
              <span className="text-muted-foreground">Payment Channel</span>
              <span className="capitalize">{transaction.payment_channel || <span className="text-muted-foreground italic">N/A</span>}</span>
           </div>
            <div className="grid grid-cols-[100px_1fr] items-center gap-4">
                <span className="text-muted-foreground">Original Desc.</span>
                <span className="text-muted-foreground break-all">{transaction.original_description || <span className="italic">N/A</span>}</span>
            </div>
            {transaction.pending_transaction_id && (
                 <div className="grid grid-cols-[100px_1fr] items-center gap-4">
                    <span className="text-muted-foreground">Pending ID</span>
                    <span className="text-muted-foreground break-all text-xs">{transaction.pending_transaction_id}</span>
                </div>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 